/**
 * Build a shareable client-portal URL that lands the recipient on a
 * specific resource after they authenticate via the self-serve magic-link
 * flow at `/portal/login`.
 *
 * Output shape: `${origin}/portal/login?redirect=${encoded(redirectPath)}`
 *
 * The redirect is validated against the frontend whitelist before being
 * appended; an invalid path is dropped and the URL falls back to the
 * plain login page. The backend repeats the same validation when minting
 * the magic-link email, so no untrusted path ever reaches the exchange
 * route.
 */

import { validateRedirectPath } from "@/config/auth";

interface BuildClientPortalUrlOptions {
  /**
   * Path the recipient should land on after auth, e.g.
   * `/client/<id>/ideas` or `/client/<id>/planning`. Must pass
   * `validateRedirectPath` to be applied.
   */
  redirectPath?: string;
  /**
   * Override origin (used for testing). Defaults to `window.location.origin`.
   */
  origin?: string;
}

export function buildClientPortalUrl(
  options: BuildClientPortalUrlOptions = {}
): string {
  const origin =
    options.origin ??
    (typeof window !== "undefined" ? window.location.origin : "");
  const base = `${origin}/portal/login`;

  if (!options.redirectPath) return base;

  const validated = validateRedirectPath(options.redirectPath);
  if (!validated) return base;

  return `${base}?redirect=${encodeURIComponent(validated)}`;
}
